import { useMutation, useQuery, useQueryClient } from "react-query";
import { z } from "zod";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { ActionPlanTemplateSchema } from "./useActionPlanTemplates";

export const TEMPLATE_NAME_MAX_LENGTH = 255;
export const CreateActionPlanTemplateSchema = z.object({
    name: z.string().trim().min(1).max(TEMPLATE_NAME_MAX_LENGTH),
    ssqId: z.number().int().positive(),
    ssqVersionId: z.number().int().positive(),
    sectorId: z.number().int().positive(),
});
export type CreateActionPlanTemplateInput = z.infer<typeof CreateActionPlanTemplateSchema>;

export const TemplateFieldValueSchema = z.object({
    actionFieldId: z.number().int().positive(),
    value: z.string(),
    position: z.number().int().nonnegative(),
    dataListOptionCodes: z.array(z.string()).nullish(),
    boundsEnabled: z.boolean().optional(),
    minimumValue: z.string().nullish(),
    maximumValue: z.string().nullish(),
    mode: z.enum(["fixed", "share"]).optional(),
    baseSsqPropertyId: z.number().int().positive().nullish(),
});
export const TemplateActionSchema = z.object({
    actionId: z.number().int().positive(),
    position: z.number().int().nonnegative(),
    fieldValues: z.array(TemplateFieldValueSchema).default([]),
});
export type TemplateAction = z.infer<typeof TemplateActionSchema>;
export type TemplateFieldValue = z.infer<typeof TemplateFieldValueSchema>;
export const UpdateActionPlanTemplateSchema = CreateActionPlanTemplateSchema.omit({
    ssqId: true,
    ssqVersionId: true,
}).extend({
    version: z.number().int().positive(),
    actions: z.array(TemplateActionSchema.omit({ position: true })),
});
export type UpdateActionPlanTemplateInput = z.infer<typeof UpdateActionPlanTemplateSchema>;

export const TemplateDetailSchema = ActionPlanTemplateSchema.extend({
    actions: z.array(TemplateActionSchema),
});

export const actionPlanTemplateQueryKey = (id: string | undefined) => ["actionPlanTemplate", id];

export const fetchActionPlanTemplate = async (id: string) => {
    const response = await loggedInApiCall.get(`/api/action-plan-templates/${id}`);
    return TemplateDetailSchema.parse(response.data);
};

export const useActionPlanTemplate = (id: string | undefined) =>
    useQuery({
        queryKey: actionPlanTemplateQueryKey(id),
        enabled: id !== undefined,
        queryFn: () => fetchActionPlanTemplate(id as string),
    });

export const useCreateActionPlanTemplate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: CreateActionPlanTemplateInput) => {
            const response = await loggedInApiCall.post(
                "/api/action-plan-templates",
                CreateActionPlanTemplateSchema.parse(input),
                { headers: { "Content-Type": "application/json" } },
            );
            return z.object({ id: z.string() }).parse(response.data);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries(["actionPlanTemplates"]);
        },
    });
};

export const useDeleteActionPlanTemplate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => loggedInApiCall.delete(`/api/action-plan-templates/${id}`),
        onSuccess: (_response, id) => {
            queryClient.removeQueries(["actionPlanTemplate", id], { exact: true });
            void queryClient.invalidateQueries(["actionPlanTemplates"]);
        },
    });
};

export type TemplateDetail = z.infer<typeof TemplateDetailSchema>;

export const useUpdateActionPlanTemplate = (id: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: UpdateActionPlanTemplateInput) => {
            const response = await loggedInApiCall.put(
                `/api/action-plan-templates/${id}`,
                UpdateActionPlanTemplateSchema.parse(input),
                { headers: { "Content-Type": "application/json" } },
            );
            return TemplateDetailSchema.parse(response.data);
        },
        onSuccess: (template) => {
            queryClient.setQueryData(["actionPlanTemplate", id], template);
            void queryClient.invalidateQueries(["actionPlanTemplates"]);
        },
    });
};
